import { formatData } from '@utilities';

import { formatChartSeries, transformColumn } from './helpers';

/**
 * loessFitAt
 *
 * Evaluates a LOESS (Locally Estimated Scatterplot Smoothing) fit at a single x position.
 * @param {number[]} xs
 * @param {number[]} ys
 * @param {number} targetX
 * @param {number} span
 * @return {number}
 */
const loessFitAt = (xs, ys, targetX, span) => {
  const neighborCount = Math.max(2, Math.round(span * xs.length));
  const bandwidth = Math.max(
    xs.map((x) => Math.abs(x - targetX)).sort((a, b) => a - b)[neighborCount - 1],
    Number.EPSILON
  );

  let sumWeight = 0;
  let sumWeightX = 0;
  let sumWeightY = 0;
  let sumWeightXX = 0;
  let sumWeightXY = 0;

  for (let index = 0; index < xs.length; index += 1) {
    const distance = Math.abs(xs[index] - targetX) / bandwidth;
    if (distance >= 1) continue;

    const weight = (1 - distance ** 3) ** 3;
    sumWeight += weight;
    sumWeightX += weight * xs[index];
    sumWeightY += weight * ys[index];
    sumWeightXX += weight * xs[index] * xs[index];
    sumWeightXY += weight * xs[index] * ys[index];
  }

  if (sumWeight === 0) return ys[Math.round(targetX)] ?? 0;

  const denominator = sumWeight * sumWeightXX - sumWeightX * sumWeightX;
  if (denominator === 0) return sumWeightY / sumWeight;

  const slope = (sumWeight * sumWeightXY - sumWeightX * sumWeightY) / denominator;
  const intercept = (sumWeightY - slope * sumWeightX) / sumWeight;
  return intercept + slope * targetX;
};

/**
 * buildVanWestendorpCurveSeries
 *
 * Converts each series' raw per-price values into `{x: price, y: value}` points.
 * @param {Object[]} series
 * @param {number[]} prices
 * @param {number} [maxY]
 * @return {Object[]}
 */
const buildVanWestendorpCurveSeries = (series, prices, maxY) => {
  if (!Array.isArray(series)) return series;

  if (prices.length < 20) {
    return series.map((s) => ({
      ...s,
      data: s.data.map((value, index) => ({ x: prices[index], y: value })),
    }));
  }

  const sampleCount = Math.min(25, prices.length);
  const sampleXs = [
    ...new Set(
      Array.from({ length: sampleCount }, (_, index) =>
        Math.round((index * (prices.length - 1)) / (sampleCount - 1))
      )
    ),
  ].map((priceIndex) => prices[priceIndex]);

  return series.map((s) => {
    const seriesMax = maxY ?? Math.max(...s.data);

    return {
      ...s,
      data: sampleXs.map((x) => ({
        marker: { enabled: true },
        x,
        y: Math.min(seriesMax, Math.max(0, loessFitAt(prices, s.data, x, 0.3))),
      })),
    };
  });
};

/**
 * buildIntersectionPlotLines
 *
 * Builds the vertical dashed lines + labels for the intersection points.
 * @param {Object} table
 * @param {number[]} prices
 * @param {string} format
 * @return {Object[]}
 */
const buildIntersectionPlotLines = (table, prices, format) => {
  const row = table?.data?.[0];
  if (!row) return [];

  const plotLineStyle = { color: '#999999', dashStyle: 'Dash', width: 1, zIndex: 5 };

  const points = table.columns
    .filter(({ accessor }) => Number.isFinite(row[accessor]))
    .map(({ accessor }) => ({
      accessor,
      price: row[accessor],
      value: row[accessor],
    }));
  const originalIndex = new Map(points.map(({ accessor }, index) => [accessor, index]));

  const priceRange = Math.max((prices?.[prices.length - 1] ?? 1) - (prices?.[0] ?? 0), 1);
  const samePositionThreshold = 0.005 * priceRange;
  const closePositionThreshold = 0.03 * priceRange;

  const groups = [];
  [...points]
    .sort((a, b) => a.price - b.price)
    .forEach((point) => {
      const last = groups[groups.length - 1];
      if (last && Math.abs(point.price - last.price) <= samePositionThreshold) {
        last.accessors.push(point.accessor);
      } else {
        groups.push({ accessors: [point.accessor], price: point.price, value: point.value });
      }
    });

  let lastAlign;
  const decorated = groups.map((group, index) => {
    const label = group.accessors.map((accessor) => accessor.toUpperCase()).join('/');
    const priceValue = row[group.accessors[0]];
    const priceText = formatData({
      format,
      value: format?.includes('%') && priceValue != null ? priceValue / 100 : priceValue,
    });
    const closeToPrevious =
      index > 0 && group.price - groups[index - 1].price <= closePositionThreshold;
    const closeToNext =
      index < groups.length - 1 && groups[index + 1].price - group.price <= closePositionThreshold;

    let align;
    if (closeToPrevious || closeToNext) {
      align = lastAlign === 'right' ? 'left' : 'right';
      lastAlign = align;
    }

    return { accessors: group.accessors, align, text: `${label} ${priceText}`, value: group.value };
  });

  decorated.sort(
    (a, b) =>
      Math.min(...a.accessors.map((accessor) => originalIndex.get(accessor))) -
      Math.min(...b.accessors.map((accessor) => originalIndex.get(accessor)))
  );

  return decorated.map(({ align, text, value }) => ({
    ...plotLineStyle,
    label: {
      text,
      ...(align && { x: align === 'right' ? -18 : 6 }),
    },
    value,
  }));
};

/**
 * transformVanWestendorpSegment
 *
 * Formats one Van Westendorp segment's chart and table data for display.
 * @param {Object} segment
 * @param {Object} segment.chart_count
 * @param {Object} segment.chart_pct
 * @param {string} segment.id
 * @param {string} segment.name
 * @param {Object} segment.table
 * @param {string} format
 * @return {Object}
 */
const transformVanWestendorpSegment = ({ chart_count, chart_pct, id, name, table }, format) => {
  const prices = (chart_pct ?? chart_count)?.xAxis?.categories?.map(Number) ?? [];
  const plotLines = buildIntersectionPlotLines(table, prices, format);
  const xAxisLabels = {
    formatter: function () {
      const value = Number(this.value);
      return formatData({ format, value: format?.includes('%') ? value / 100 : value });
    },
  };

  const buildChart = (chart, maxY) => {
    const formattedChart = formatChartSeries(chart);
    const { categories: _categories, ...xAxisWithoutCategories } = formattedChart?.xAxis ?? {};

    return (
      formattedChart && {
        ...formattedChart,
        series: buildVanWestendorpCurveSeries(formattedChart.series, prices, maxY),
        xAxis: {
          ...xAxisWithoutCategories,
          labels: xAxisLabels,
          max: prices[prices.length - 1],
          min: prices[0],
          plotLines,
        },
      }
    );
  };

  return {
    chartCount: buildChart(chart_count),
    chartPct: buildChart(chart_pct, 1),
    id,
    name,
    table: table && {
      columns: table.columns.map((column) => {
        const resolvedFormat = column.format ?? format;
        return {
          ...transformColumn({ ...column, format: resolvedFormat }),
          ...(resolvedFormat && {
            Cell: ({ value }) =>
              formatData({
                format: resolvedFormat,
                value: resolvedFormat?.includes('%') && value != null ? value / 100 : value,
              }),
          }),
        };
      }),
      data: table.data,
    },
  };
};

/**
 * transformVanWestendorpResponses
 *
 * Transforms the raw Van Westendorp survey response into the
 * chart and table data the dashboard renders.
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformVanWestendorpResponses = ({ data }) => {
  return data.map(({ id, question_format: questionFormat, segments, shortname }) => {
    const format = questionFormat ?? '$0,0.00';

    return {
      id: Number(id),
      segments: segments.map((segment) => transformVanWestendorpSegment(segment, format)),
      shortname,
    };
  });
};
